import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import RichTextEditor from "../../Common/TinyMCE/RichTextEditor";
import "flatpickr/dist/themes/material_green.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getContentById,
  addContent,
  updateContent,
} from "../../../services/contentsService";
import { getContentCategoriesList } from "../../../services/contentCategoriesService";
import { getContentTopicsList } from "../../../services/contentTopicsService";
import { validateContent } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { buildContentFormData, mapContentResponse } from "./contentFormUtils";
import AiGenerate from "../../AI/AiGenerate";
import { CONTENT_AI_RESPONSE_FIELD_MAP } from "../../AI/AIResponseFieldMap";
import "../../AI/ai-generator.css";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { Loading } from "../../Common/OtherElements/Loading";
import ImageUpload from "../../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";

const INITIAL_FORM_STATE = {
  contentCategoryId: "",
  contentTopicId: "",
  title: "",
  contentSlug: "",
  shortDescription: "",
  fullDescription: "",
  contentImageFile: null,
  contentImagePreview: "",
  contentImageUrl: "",
  bannerImageFile: null,
  bannerImagePreview: "",
  bannerImageUrl: "",
  detailPageUrl: "",
  authorName: "",
  sourceName: "",
  readingTime: "",
  publishedOn: "",
  eventsFrom: "",
  eventsTo: "",
  eventLocation: "",
  isFeatured: false,
  isFeaturedHome: false,
  isPremium: false,
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
};

const INITIAL_ERRORS_STATE = {
  contentCategoryId: "",
  title: "",
  contentSlug: "",
  shortDescription: "",
  fullDescription: "",
  contentImage: "",
  bannerImage: "",
  detailPageUrl: "",
  displayOrder: "",
  publishedOn: "",
  eventsFrom: "",
  eventsTo: "",
  metaTitle: "",
  metaDescription: "",
};

const DATETIME_PICKER_OPTIONS = {
  enableTime: true,
  dateFormat: "Y-m-d H:i",
  time_24hr: true,
  monthSelectorType: "static",
};

const TINYMCE_INIT = {
  height: 450,
  menubar: false,
  plugins: [
    "advlist",
    "autolink",
    "link",
    "lists",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "wordcount",
    "visualblocks",
    "code",
    "fullscreen",
    "insertdatetime",
    "table",
    "help",
  ],
  toolbar:
    "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | preview code fullscreen | help",
};

const mapDropdownOptions = (
  list,
  nameKey,
  pascalNameKey,
  idKey = "id",
  pascalIdKey = "Id",
) =>
  (list || []).map((item) => ({
    value: String(item[idKey] ?? item[pascalIdKey] ?? ""),
    label: item[nameKey] || item[pascalNameKey] || "-",
  }));

const resolveDropdownLabel = (options, selectedId) => {
  const match = (options || []).find(
    (opt) => opt.value === String(selectedId ?? ""),
  );
  return (match?.label || "").trim();
};

export const AddContent = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [pageLevelAccessUrl, setPageLevelAccessUrl] = useState("");
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const slugManuallyEdited = useRef(false);
  const [selectedMedia, setSelectedMedia] = useState({});

  const aiPageLink = useMemo(() => {
    const path = (location.pathname || "").replace(/^\//, "").toLowerCase();
    if (/^contents\/update\/\d+$/.test(path)) {
      return "contents/update/:id";
    }
    if (path === "contents/add") {
      return "contents/add";
    }
    return id ? "contents/update/:id" : "contents/add";
  }, [location.pathname, id]);

  const contentAIRequiredFields = useMemo(
    () => [
      {
        getValue: (fd) =>
          resolveDropdownLabel(categories, fd.contentCategoryId),
        minLength: 1,
        errorMsg: "Please Select Content Category",
        dynamicFieldKey: "ContentCategory",
      },
      {
        getValue: (fd) => resolveDropdownLabel(topics, fd.contentTopicId),
        minLength: 1,
        errorMsg: "Please Select Content Topic",
        dynamicFieldKey: "ContentTopic",
      },
      {
        getValue: (fd) => fd.title,
        minLength: 5,
        errorMsg: "Please Enter Title To Generate",
        dynamicFieldKey: "Title",
      },
    ],
    [categories, topics],
  );

  useEffect(() => {
    if (id) {
      setPageLevelAccessUrl("contents/update/:id");
    } else {
      setPageLevelAccessUrl("contents/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else if (!pageAccessData.addAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, id, navigate]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [catRes, topicRes] = await Promise.all([
          getContentCategoriesList(),
          getContentTopicsList(),
        ]);
        const catList = catRes.data?.result || catRes.data || [];
        const topicList = topicRes.data?.result || topicRes.data || [];
        setCategories(
          mapDropdownOptions(
            Array.isArray(catList) ? catList : [],
            "categoryName",
            "CategoryName",
          ),
        );
        setTopics(
          mapDropdownOptions(
            Array.isArray(topicList) ? topicList : [],
            "topicName",
            "TopicName",
          ),
        );
      } catch (error) {
        handleErrors(error);
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await getContentById(id);
          setFormData(mapContentResponse(data));
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
    };
    fetchContent();
  }, [id]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "title") {
      setFormData((prev) => ({
        ...prev,
        title: value,
        ...(slugManuallyEdited.current
          ? {}
          : { contentSlug: generateSlug(value) }),
      }));
    } else if (name === "contentSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, contentSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleEditorChange = useCallback((content) => {
    setFormData((prev) => ({
      ...prev,
      fullDescription: content ?? "",
    }));
    setErrors((prev) => ({ ...prev, fullDescription: "" }));
  }, []);

  const aiEditorHandlers = useMemo(
    () => ({
      fullDescription: handleEditorChange,
    }),
    [handleEditorChange],
  );

  const handleDateChange = (name, dates, dateStr) => {
    const value = dateStr || "";
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMediaSelect = (media, fieldKey, previewKey, urlKey, errorKey) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: null,
      [previewKey]: url,
      [urlKey]: url,
    }));
    setSelectedMedia((prev) => ({ ...prev, [fieldKey]: media }));
    setErrors((prev) => ({ ...prev, [errorKey]: "" }));
  };

  const handleMediaRemove = (fieldKey, previewKey, urlKey, errorKey) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: null,
      [previewKey]: "",
      [urlKey]: "",
    }));
    setSelectedMedia((prev) => ({ ...prev, [fieldKey]: null }));
    if (errorKey) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  const contentImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.contentImageFile,
    imageUrl: formData.contentImageUrl,
    defaultKeyword: formData.title,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "contentImageFile",
        "contentImagePreview",
        "contentImageUrl",
        "contentImage",
      ),
    onRemove: () =>
      handleMediaRemove(
        "contentImageFile",
        "contentImagePreview",
        "contentImageUrl",
        "contentImage",
      ),
  });

  const bannerImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia.bannerImageFile,
    imageUrl: formData.bannerImageUrl,
    defaultKeyword: formData.title,
    onSelect: (media) =>
      handleMediaSelect(
        media,
        "bannerImageFile",
        "bannerImagePreview",
        "bannerImageUrl",
        "bannerImage",
      ),
    onRemove: () =>
      handleMediaRemove(
        "bannerImageFile",
        "bannerImagePreview",
        "bannerImageUrl",
        "bannerImage",
      ),
  });

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateContent(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const payload = buildContentFormData(formData, isEditMode, id);

      try {
        setIsButtonDisabled(true);
        const action = isEditMode ? updateContent : addContent;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again.",
          );
          return;
        }

        toast.success(
          `Content ${isEditMode ? "updated" : "added"} successfully!`,
        );
        setTimeout(() => {
          navigate("/contents");
        }, 2000);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, isEditMode, id, navigate],
  );

  const handleAiAfterInject = useCallback(() => {
    setFormData((prev) => {
      if (!slugManuallyEdited.current && prev.title?.trim()) {
        return {
          ...prev,
          contentSlug: generateSlug(String(prev.title).trim()),
        };
      }
      return prev;
    });
  }, []);

  if (loading && isEditMode) {
    return <Loading />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">
              {isEditMode ? "Update Content" : "Add Content"}
            </h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/contents">Contents</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? `Update #${id}` : "Add"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-lg-8">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-0">Basic Information</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="contentCategoryId" className="form-label">
                        Content Category{" "}
                        <span className="required-field">*</span>
                      </label>
                      <select
                        name="contentCategoryId"
                        id="contentCategoryId"
                        value={formData.contentCategoryId}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.contentCategoryId ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select category</option>
                        {categories.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.contentCategoryId && (
                        <div className="invalid-feedback">
                          {errors.contentCategoryId}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="contentTopicId" className="form-label">
                        Content Topic
                      </label>
                      <select
                        name="contentTopicId"
                        id="contentTopicId"
                        value={formData.contentTopicId}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select topic (optional)</option>
                        {topics.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="title" className="form-label">
                        Title <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        placeholder="Enter content title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.title ? "is-invalid" : ""
                        }`}
                      />
                      {errors.title && (
                        <div className="invalid-feedback">{errors.title}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="contentSlug" className="form-label">
                        Content Slug <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="contentSlug"
                        id="contentSlug"
                        placeholder="content-slug"
                        value={formData.contentSlug}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.contentSlug ? "is-invalid" : ""
                        }`}
                      />
                      {errors.contentSlug && (
                        <div className="invalid-feedback">
                          {errors.contentSlug}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="shortDescription" className="form-label">
                        Short Description
                      </label>
                      <textarea
                        name="shortDescription"
                        id="shortDescription"
                        rows="4"
                        placeholder="Brief summary for listings"
                        value={formData.shortDescription}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.shortDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.shortDescription && (
                        <div className="invalid-feedback">
                          {errors.shortDescription}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-0">
                      <label htmlFor="fullDescription" className="form-label">
                        Full Description{" "}
                        <span className="required-field">*</span>
                      </label>
                      <RichTextEditor
                        value={formData.fullDescription}
                        onEditorChange={handleEditorChange}
                        defaultKeyword={formData.title}
                      />
                      {errors.fullDescription && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: ".875em" }}
                        >
                          {errors.fullDescription}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="mb-0">Media</h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-md-6">
                    <ImageUpload
                      label="Content Image"
                      previewUrl={formData.contentImagePreview}
                      error={errors.contentImage}
                      {...contentImageLibrary.uploadEnhancements}
                    />
                    {contentImageLibrary.modalSlot}
                  </div>
                  <div className="col-md-6">
                    <ImageUpload
                      label="Banner Image"
                      previewUrl={formData.bannerImagePreview}
                      error={errors.bannerImage}
                      {...bannerImageLibrary.uploadEnhancements}
                    />
                    {bannerImageLibrary.modalSlot}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="mb-0">Publishing</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="detailPageUrl" className="form-label">
                        Detail Page URL
                      </label>
                      <input
                        type="url"
                        name="detailPageUrl"
                        id="detailPageUrl"
                        placeholder="https://example.com/article"
                        value={formData.detailPageUrl}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.detailPageUrl ? "is-invalid" : ""
                        }`}
                      />
                      {errors.detailPageUrl && (
                        <div className="invalid-feedback">
                          {errors.detailPageUrl}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="publishedOn" className="form-label">
                        Published On
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.publishedOn ? "is-invalid" : ""
                        }`}
                        placeholder="Select date & time"
                        value={formData.publishedOn}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("publishedOn", dates, dateStr)
                        }
                      />
                      {errors.publishedOn && (
                        <div className="invalid-feedback d-block">
                          {errors.publishedOn}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="authorName" className="form-label">
                        Author Name
                      </label>
                      <input
                        type="text"
                        name="authorName"
                        id="authorName"
                        value={formData.authorName}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="sourceName" className="form-label">
                        Source Name
                      </label>
                      <input
                        type="text"
                        name="sourceName"
                        id="sourceName"
                        value={formData.sourceName}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="readingTime" className="form-label">
                        Reading Time
                      </label>
                      <input
                        type="text"
                        name="readingTime"
                        id="readingTime"
                        placeholder="e.g. 5 min read"
                        value={formData.readingTime}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="mb-0">Event Settings</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="eventsFrom" className="form-label">
                        Events From
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.eventsFrom ? "is-invalid" : ""
                        }`}
                        placeholder="Start date & time"
                        value={formData.eventsFrom}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("eventsFrom", dates, dateStr)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="eventsTo" className="form-label">
                        Events To
                      </label>
                      <Flatpickr
                        className={`form-control ${
                          errors.eventsTo ? "is-invalid" : ""
                        }`}
                        placeholder="End date & time"
                        value={formData.eventsTo}
                        options={DATETIME_PICKER_OPTIONS}
                        onChange={(dates, dateStr) =>
                          handleDateChange("eventsTo", dates, dateStr)
                        }
                      />
                      {errors.eventsTo && (
                        <div className="invalid-feedback d-block">
                          {errors.eventsTo}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="eventLocation" className="form-label">
                        Event Location
                      </label>
                      <input
                        type="text"
                        name="eventLocation"
                        id="eventLocation"
                        value={formData.eventLocation}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion custom-accordionwithicon accordion-flush mt-3">
              <div className="accordion-item">
                <h2 className="accordion-header" id="contentSeoHeading">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#contentSeoCollapse"
                    aria-expanded="false"
                    aria-controls="contentSeoCollapse"
                  >
                    <i className="ri-search-eye-line me-2"></i>
                    SEO Settings
                  </button>
                </h2>
                <div
                  id="contentSeoCollapse"
                  className="accordion-collapse collapse"
                  aria-labelledby="contentSeoHeading"
                >
                  <div className="accordion-body">
                    <div className="mb-3">
                      <label htmlFor="metaTitle" className="form-label">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        name="metaTitle"
                        id="metaTitle"
                        value={formData.metaTitle}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaTitle ? "is-invalid" : ""
                        }`}
                      />
                      {errors.metaTitle && (
                        <div className="invalid-feedback">
                          {errors.metaTitle}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="metaDescription" className="form-label">
                        Meta Description
                      </label>
                      <textarea
                        name="metaDescription"
                        id="metaDescription"
                        rows="4"
                        value={formData.metaDescription}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.metaDescription && (
                        <div className="invalid-feedback">
                          {errors.metaDescription}
                        </div>
                      )}
                    </div>
                    <div className="mb-0">
                      <label htmlFor="metaKeywords" className="form-label">
                        Meta Keywords
                      </label>
                      <textarea
                        name="metaKeywords"
                        id="metaKeywords"
                        rows="3"
                        value={formData.metaKeywords}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-0">Display Settings</h5>
              </div>
              <div className="card-body p-4">
                <div className="mb-3">
                  <label className="form-label d-block">Is Featured</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeatured"
                      name="isFeatured"
                      checked={formData.isFeatured}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label d-block">Featured Home</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeaturedHome"
                      name="isFeaturedHome"
                      checked={formData.isFeaturedHome}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label d-block">Is Premium</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isPremium"
                      name="isPremium"
                      checked={formData.isPremium}
                      onChange={handleToggleChange}
                    />
                  </div>
                </div>
                <div className="mb-0">
                  <label htmlFor="displayOrder" className="form-label">
                    Display Order <span className="required-field">*</span>
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    id="displayOrder"
                    min="1"
                    step="1"
                    value={formData.displayOrder}
                    onChange={handleDisplayOrderChange}
                    onKeyDown={handleDisplayOrderKeyDown}
                    className={`form-control ${
                      errors.displayOrder ? "is-invalid" : ""
                    }`}
                  />
                  {errors.displayOrder && (
                    <div className="invalid-feedback">
                      {errors.displayOrder}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <AiGenerate
              pageLink={aiPageLink}
              formData={formData}
              setFormData={setFormData}
              requiredFields={contentAIRequiredFields}
              fieldMap={CONTENT_AI_RESPONSE_FIELD_MAP}
              editorFields={["fullDescription"]}
              editorHandlers={aiEditorHandlers}
              onAfterInject={handleAiAfterInject}
              title="Generate Content"
            />
          </div>
        </div>

        <div className="row mt-3 mb-4">
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-secondary pt-1 pb-1 p-3"
              disabled={isButtonDisabled}
            >
              {isButtonDisabled
                ? isEditMode
                  ? "Updating"
                  : "Saving"
                : isEditMode
                  ? "Update"
                  : "Save"}
            </button>
            <Link to="/contents" className="btn btn-light ms-2 pt-1 pb-1 p-3">
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </>
  );
};
